import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { ApiError, checkEmailExists } from "@/lib/api";
import { GoogleButton } from "@/components/ui/GoogleButton";
import { Slides } from "@/components/ui/Slides";
import { usePageTitle } from "@/hooks/usePageTitle";
import logo from "@/assets/famibelleza_logo.png";

const inputClass =
  "w-full rounded-lg border border-app-border bg-app-control px-3 py-2 text-foreground focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand";

export default function Login() {
  const { t } = useTranslation();
  usePageTitle(t("login.title"));
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "password">("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorKey, setErrorKey] = useState<
    "emailNotFound" | "incorrectPassword" | "tooManyAttempts" | null
  >(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmitEmail = async (e: FormEvent) => {
    e.preventDefault();
    setErrorKey(null);
    setSubmitting(true);
    try {
      const exists = await checkEmailExists(email);
      if (exists) {
        setStep("password");
      } else {
        setErrorKey("emailNotFound");
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        setErrorKey("tooManyAttempts");
        return;
      }
      // Couldn't verify the email (network/server issue). Don't block login
      // over a check that's only advisory — let them try the password; the
      // login call itself is the real source of truth.
      setStep("password");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitPassword = async (e: FormEvent) => {
    e.preventDefault();
    setErrorKey(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (error) {
      setErrorKey(
        error instanceof ApiError && error.status === 429
          ? "tooManyAttempts"
          : "incorrectPassword"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onBack = () => {
    setStep("email");
    setPassword("");
    setErrorKey(null);
  };

  return (
    <div className="relative flex items-center justify-center py-12 px-4 overflow-hidden">
      <img src={logo} alt="" className="absolute top-0 w-1/2 h-full object-contain z-0 opacity-10 blur-md" />
      <div className="relative z-10 max-w-md w-full bg-app-surface text-foreground border border-app-border rounded-2xl shadow-md p-8 space-y-6">
        <div className="grid grid-cols-[2rem_1fr_2rem] items-center">
          {step === "password" ? (
            <button
              type="button"
              onClick={onBack}
              aria-label={t("login.back")}
              className="flex items-center justify-center w-8 h-8 rounded-full text-app-muted hover:bg-app-surface-hover cursor-pointer"
            >
              <ChevronLeft size={20} />
            </button>
          ) : (
            <span />
          )}
          <h1 className="text-center text-3xl font-extrabold text-brand">{t("login.title")}</h1>
          <span />
        </div>

        {step === "email" && (
          <p className="text-center text-sm text-app-muted">
            {t("login.noAccount")}{" "}
            <Link to="/register" className="font-medium text-brand hover:text-brand/80">
              {t("login.createOne")}
            </Link>
          </p>
        )}

        {step === "password" && (
          <button
            type="button"
            onClick={onBack}
            className="w-full flex items-center gap-3 rounded-full border border-app-border bg-app-control px-3 py-2 hover:bg-app-control-hover cursor-pointer text-left"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand text-white text-sm font-semibold uppercase shrink-0">
              {email.charAt(0)}
            </span>
            <span className="text-sm text-foreground truncate">{email}</span>
          </button>
        )}

        <Slides step={step === "email" ? 0 : 1}>
          {[
            <div className="flex flex-col gap-4" key="email">
              <form onSubmit={onSubmitEmail} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label htmlFor="email" className="text-sm font-medium text-foreground">{t("common.email")}</label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoFocus
                    className={inputClass}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>

                {errorKey === "emailNotFound" && (
                  <div className="rounded-md bg-red-500/10 p-3">
                    <p className="text-sm font-medium text-red-700 dark:text-red-300">{t("login.emailNotFound")}</p>
                  </div>
                )}

                {errorKey === "tooManyAttempts" && (
                  <div className="rounded-md bg-red-500/10 p-3">
                    <p className="text-sm font-medium text-red-700 dark:text-red-300">{t("login.tooManyAttempts")}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 px-4 bg-brand text-white font-semibold rounded-2xl cursor-pointer hover:bg-brand/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {submitting ? t("login.checkingEmail") : t("login.continue")}
                </button>
              </form>

              <GoogleButton />
            </div>,

            <form onSubmit={onSubmitPassword} className="flex flex-col gap-4" key="password">
              <div className="flex flex-col gap-1">
                <label htmlFor="password" className="text-sm font-medium text-foreground">{t("common.password")}</label>
                <input
                  id="password"
                  type="password"
                  required
                  autoFocus
                  className={inputClass}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <div className="mt-1 text-right">
                  <Link to="/forgot-password" className="text-sm text-brand hover:text-brand/80">{t("login.forgotPassword")}</Link>
                </div>
              </div>

              {errorKey === "incorrectPassword" && (
                <div className="rounded-md bg-red-500/10 p-3">
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">{t("login.incorrectPassword")}</p>
                </div>
              )}

              {errorKey === "tooManyAttempts" && (
                <div className="rounded-md bg-red-500/10 p-3">
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">{t("login.tooManyAttempts")}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 px-4 bg-brand text-white font-semibold rounded-2xl cursor-pointer hover:bg-brand/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {submitting ? t("login.submitting") : t("login.submit")}
              </button>
            </form>,
          ]}
        </Slides>
      </div>
    </div>
  );
}
